import { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api.js';

interface Account { id: string; name: string; region: string; is_active?: number }
interface LogEntry {
  id: string; algorithm_type: string; rule_name: string; campaign_name: string;
  keyword_text: string | null; search_term: string | null; change_type: string;
  old_value: string; new_value: string; mode: string; condition_values: string;
  details: string; created_at: string; marketplace: string;
}

const ALGO_TABS = [
  { key: '', label: 'All' },
  { key: 'bidding', label: 'Bidding' },
  { key: 'negative', label: 'Negative' },
  { key: 'import', label: 'Import' },
  { key: 'negative_word', label: 'Neg. Word' },
  { key: 'status', label: 'Status' },
  { key: 'budget', label: 'Budget' },
  { key: 'dayparting', label: 'Dayparting' },
  { key: 'revive', label: 'Revive' },
  { key: 'placement', label: 'Placement' },
  { key: 'default_bid', label: 'Default Bid' },
];

const ALGO_COLORS: Record<string, string> = {
  bidding: 'blue', negative: 'red', import: 'green', status: 'yellow',
  budget: 'purple', dayparting: 'orange', revive: 'teal',
  placement: 'indigo', default_bid: 'gray', negative_word: 'pink',
};

const CHANGE_LABELS: Record<string, string> = {
  bid_change: 'Bid Change', status_change: 'Status Change', negative_added: 'Negative Added',
  keyword_imported: 'Keyword Import', budget_change: 'Budget Change', placement_change: 'Placement Change',
};

export function PpcChangeLog() {
  const [accountId, setAccountId] = useState('');
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [algoFilter, setAlgoFilter] = useState('');
  const [modeFilter, setModeFilter] = useState<'' | 'preview' | 'live'>('');
  const [daysBack, setDaysBack] = useState(7);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [summary, setSummary] = useState<Array<{ algorithm_type: string; mode: string; change_type: string; cnt: number }>>([]);

  useEffect(() => {
    api.get<Account[]>('/accounts').then(accts => {
      const active = accts.filter(a => (a as Account & { is_active?: number }).is_active !== 0);
      if (active.length > 0) setAccountId(active[0].id);
    });
  }, []);

  const loadLog = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({
        accountId, daysBack: String(daysBack), limit: '200',
      });
      if (algoFilter) params.set('algorithmType', algoFilter);
      if (modeFilter) params.set('mode', modeFilter);

      const res = await api.get<{ entries: LogEntry[] }>(`/automation/log?${params}`);
      setEntries(res.entries);

      // Load summary separately (unfiltered)
      const sumRes = await api.get<{ recentActivity: typeof summary }>(`/automation/status?accountId=${accountId}`);
      setSummary(sumRes.recentActivity);
    } catch { /* noop */ }
    setLoading(false);
  }, [accountId, algoFilter, modeFilter, daysBack]);

  useEffect(() => { loadLog(); }, [loadLog]);

  // Summary cards
  let liveCount = 0, previewCount = 0;
  const byType = new Map<string, number>();
  for (const s of summary) {
    if (s.mode === 'live') { liveCount += s.cnt; byType.set(s.change_type, (byType.get(s.change_type) ?? 0) + s.cnt); }
    else previewCount += s.cnt;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Change Log</h1>
          <p className="mt-1 text-sm text-gray-500">Full audit trail of all automation changes — preview and live executions</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Live Actions (7d)</p>
          <p className="mt-1 text-2xl font-bold text-green-600">{liveCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Previewed (7d)</p>
          <p className="mt-1 text-2xl font-bold text-yellow-600">{previewCount}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Bid Changes</p>
          <p className="mt-1 text-2xl font-bold text-blue-600">{byType.get('bid_change') ?? 0}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <p className="text-xs text-gray-500">Negatives Added</p>
          <p className="mt-1 text-2xl font-bold text-red-600">{byType.get('negative_added') ?? 0}</p>
        </div>
      </div>

      {/* Algorithm Tabs — Scale Insights style */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-1 overflow-x-auto">
          {ALGO_TABS.map(t => (
            <button key={t.key} onClick={() => setAlgoFilter(t.key)}
              className={`whitespace-nowrap border-b-2 px-3 pb-2 text-sm font-medium transition-colors ${
                algoFilter === t.key ? 'border-brand-600 text-brand-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              {t.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3">
        <select value={daysBack} onChange={e => setDaysBack(Number(e.target.value))}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value={1}>Last 24 hours</option>
          <option value={3}>Last 3 days</option>
          <option value={7}>Last 7 days</option>
          <option value={14}>Last 14 days</option>
          <option value={30}>Last 30 days</option>
        </select>
        <select value={modeFilter} onChange={e => setModeFilter(e.target.value as '' | 'preview' | 'live')}
          className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm">
          <option value="">All modes</option>
          <option value="live">Live only</option>
          <option value="preview">Preview only</option>
        </select>
        <button onClick={loadLog}
          className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50">
          Refresh
        </button>
        <span className="text-xs text-gray-400">{entries.length} entries</span>
      </div>

      {loading && <div className="py-8 text-center text-gray-500">Loading...</div>}

      {/* Log Table */}
      {!loading && (
        <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-gray-100 bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Time (UTC)</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Algorithm</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Rule</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Campaign</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Target</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Change</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Value</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Mode</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {entries.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-gray-400">
                  No changes in this period. Configure automation rules via Claude or the Rules page.
                </td></tr>
              )}
              {entries.map(e => {
                const color = ALGO_COLORS[e.algorithm_type] ?? 'gray';
                const isExpanded = expandedId === e.id;
                return (
                  <>
                    <tr key={e.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : e.id)}>
                      <td className="px-3 py-2 text-xs text-gray-500 whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium bg-${color}-100 text-${color}-700`}>
                          {ALGO_TABS.find(t => t.key === e.algorithm_type)?.label ?? e.algorithm_type}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700 max-w-[150px] truncate">{e.rule_name}</td>
                      <td className="px-3 py-2 text-gray-600 max-w-[150px] truncate" title={e.campaign_name}>{e.campaign_name}</td>
                      <td className="px-3 py-2 text-xs">
                        {e.keyword_text && <span className="text-gray-700">{e.keyword_text}</span>}
                        {e.search_term && <span className="text-gray-500 italic">{e.search_term}</span>}
                        {!e.keyword_text && !e.search_term && <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-3 py-2 text-xs text-gray-600">{CHANGE_LABELS[e.change_type] ?? e.change_type}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="text-gray-400">{e.old_value || '—'}</span>
                        <span className="mx-1 text-gray-300">&rarr;</span>
                        <span className="font-medium text-gray-800">{e.new_value}</span>
                      </td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                          e.mode === 'live' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                        }`}>{e.mode}</span>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr key={`${e.id}-detail`} className="bg-gray-50">
                        <td colSpan={8} className="px-6 py-3">
                          <div className="grid gap-4 sm:grid-cols-2 text-xs">
                            <div>
                              <p className="font-medium text-gray-600 mb-1">Condition Values</p>
                              <pre className="rounded bg-white p-2 border border-gray-200 text-[11px] overflow-auto max-h-32">
                                {JSON.stringify(JSON.parse(e.condition_values || '{}'), null, 2)}
                              </pre>
                            </div>
                            <div>
                              <p className="font-medium text-gray-600 mb-1">Details</p>
                              <pre className="rounded bg-white p-2 border border-gray-200 text-[11px] overflow-auto max-h-32">
                                {JSON.stringify(JSON.parse(e.details || '{}'), null, 2)}
                              </pre>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
